import Glide from "@glidejs/glide";
import Rating from "components/Rating";
import { TopSellingProduct } from "entities/HomePageBannerList";
import React, { useEffect, useId, useRef, useState } from "react";
import ButtonCSCLed from "shared/Button/ButtonCSCLed";
import NcImage from "shared/NcImage/NcImage";
import NcLink from "shared/NcLink/NcLink";
import NextPrev from "shared/NextPrev/NextPrev";
import { useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { config } from "utils/Constance";
import { stringToSlug } from "utils/stringToSlug";

type HomePageBestSellersProps = {
  productList: TopSellingProduct[];
  productTitle?: string;
};

const HomePageBestSellers = ({
  productList,
  productTitle,
}: HomePageBestSellersProps) => {
  const sliderRef = useRef(null);
  const id = useId();
  const UNIQUE_CLASS = "glidejs" + id.replace(/:/g, "_");
  //const userData = getUserLoginData();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 500);
  const user = useAppSelector((state: RootState) => state.auth);

  useEffect(() => {
    if (!sliderRef.current) {
      return () => {};
    }

    // @ts-ignore
    const OPTIONS: Glide.Options = {
      perView: 4,
      gap: 22,
      bound: true,
      breakpoints: {
        1280: {
          perView: 4 - 1,
        },
        1024: {
          gap: 20,
          perView: 2.5,
        },
        768: {
          gap: 20,
          perView: 4 - 2,
        },
        640: {
          gap: 20,
          perView: 1.5,
        },
        500: {
          gap: 20,
          perView: 1.1,
        },
      },
    };

    let slider = new Glide(`.${UNIQUE_CLASS}`, OPTIONS);
    slider.mount();
    return () => {
      slider.destroy();
    };
  }, [sliderRef, UNIQUE_CLASS]);

  const productUrl = (productNumber: string) =>
    `/product-details/${stringToSlug(productNumber).toUpperCase()}`;

  const handleWindowSizeChange = () => {
    setIsMobile(window.innerWidth <= 500);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const HeaderSection = () => {
    return (
      <div className="nc-Section-Heading text-csccolor relative flex flex-col sm:flex-row sm:items-end justify-center text-center pb-10 dark:text-neutral-50 w-full">
        <h1 className="text-3xl md:text-4xl font-semibold font-poppins">
          Weekly Best Sellers {!isMobile && `- ${productTitle}`}
        </h1>
        <h5 className="sm:hidden text-lg font-medium">{productTitle}</h5>
      </div>
    );
  };

  const LocationQty = (prod: TopSellingProduct) => {
    const renderBranch = (label: string, value: number, tooltip: string) => {
      return (
        <div className="flex flex-col mt-2">
          <span className="text-csccolor mb-1 font-semibold" title={tooltip}>
            {label} Qty
          </span>
          <span>{value}</span>
        </div>
      );
    };

    return (
      <div className="nc-LoactionQty px-5 grid grid-cols-3 gap-6 text-sm lg:text-base">
        {renderBranch("AB", prod.abQty, "Alberta")}
        {renderBranch("BC", prod.bcQty, "British Columbia")}
        {renderBranch("ON", prod.onQty, "Ontario")}
      </div>
    );
  };

  const ProductCard = (prod: TopSellingProduct) => {
    return (
      <div
        key={prod.id}
        className="bg-white shadow rounded-xl p-4 flex flex-col items-center"
      >
        <NcImage
          containerClassName="w-fit h-44 sm:h-56"
          src={`${config.url.ERP_Domain}${prod.lowResImage.replace(
            "../../",
            ""
          )}`}
          className="object-contain w-full h-full"
        />
        <div className="flex flex-col text-center items-center space-y-2">
          <NcLink
            to={productUrl(prod.productNumber)}
            className="capitalize text-csccolor font-bold text-lg"
            title={"View Product"}
          >
            {prod.productNumber}
          </NcLink>
          <Rating size={25} count={prod.rating} value={prod.rating} />
          <div
            className={`flex flex-col text-center text-sm xl:text-base ${user.isAuthenticated ? `h-36 xl:h-52`:`h-24 xl:h-32`}`}
          >
            <p>{prod.productDescription}</p>
            {user.isAuthenticated && <LocationQty {...prod} />}
          </div>
          <div className="mt-auto py-4">
            <NcLink to={productUrl(prod.productNumber)}>
              <ButtonCSCLed className="w-full sm:w-fit">
                View Product
              </ButtonCSCLed>
            </NcLink>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="nc-HomePageBestSellers w-full flex flex-col bg-stone-100 pt-10 px-3 sm:px-16">
      <HeaderSection />
      <div className={`${UNIQUE_CLASS} flow-root`} ref={sliderRef}>
        <div className="glide__track" data-glide-el="track">
          <ul className="glide__slides">
            {productList.map((item, index) => (
              <li key={index} className="glide__slide mb-7">
                <ProductCard {...item} />
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center justify-center mb-7">
          <NextPrev onClickNext={() => {}} onClickPrev={() => {}} />
        </div>
      </div>
    </div>
  );
};

export default HomePageBestSellers;
